import { TaskAPI } from 'api/tasks';
import { getProjectFromCollection, projectCollection } from 'collections/project';
import { converToEntityId, PhaseEntityType } from 'data/projects/phase-entity';
import { logger } from 'services';
import { negativeUid } from 'utils/uid';
import { mapTaskChangesToPayload } from './payload';
import { taskCollection } from './task-collection';
import { taskFunnel } from './telemetry';
/** Narrows an untyped API response to `Task[]` before pushing. */ const isTaskArray = (value)=>Array.isArray(value);
/** Narrows an untyped API response to a single `Task` before pushing. */ const isTask = (value)=>typeof value === 'object' && value !== null && typeof value.taskId === 'number';
/**
 * Action helpers for cases that are not a single-field task update:
 * bulk edits, optimistic create, WS sync, dependencies, ratings.
 *
 * Single-field updates / deletes go through `taskCollection.update` /
 * `.delete` directly (or the `useCollectionMutation` hook).
 */ /**
 * Optimistically create a task. Uses a temporary negative id (matching the
 * existing `negativeUid()` convention); the collection swaps it for the
 * server id once `createTask` resolves.
 */ /**
 * Optimistic task field op-update from a UI surface (task drawer sections,
 * pickers). Wraps `taskCollection.update` with funnel telemetry: emits
 * `task.field_update` on apply and `task.field_update.error` if the server
 * rejects and the overlay rolls back. Reflects on whichever page is mounted
 * (plan / list) since both read this collection. Use this instead of a raw
 * `taskCollection.update` for user-driven single-task edits.
 */ export const optimisticTaskFieldUpdate = (taskId, payload, kind = 'field')=>{
    taskFunnel.fieldUpdate({
        taskId,
        fields: Object.keys(payload !== null && payload !== void 0 ? payload : {}),
        kind
    });
    const tx = taskCollection.update(taskId, payload);
    tx.isPersisted.catch(()=>taskFunnel.fieldUpdateError({
            taskId,
            kind
        }));
    return tx;
};
export const createTaskOptimistic = (projectId, draft)=>{
    var _draft_project, _ref;
    var _draft_parentTaskObject;
    const tempTask = {
        ...draft,
        taskId: negativeUid(),
        project: {
            ...(_draft_project = draft.project) !== null && _draft_project !== void 0 ? _draft_project : {},
            projectId
        }
    };
    // Funnel: a parented draft is a subtask add. Optimistic insert reflects on
    // whichever page is mounted (plan / list) since both read this collection.
    const parentTaskId = (_ref = (_draft_parentTaskObject = draft.parentTaskObject) === null || _draft_parentTaskObject === void 0 ? void 0 : _draft_parentTaskObject.taskId) !== null && _ref !== void 0 ? _ref : draft.parentTask;
    if (parentTaskId != null) {
        taskFunnel.subtaskAdd({
            parentTaskId,
            optimistic: true
        });
    }
    return taskCollection.insert(tempTask);
};
/**
 * Bulk update many tasks (known ids) in a single API call, optimistically.
 * All changes roll back together if the bulk call fails.
 *
 * The bulk endpoint returns no bodies; visible state stays optimistic until a
 * WS refetch reconciles it (consistent with the WS=refetch decision).
 */ export const bulkUpdateTasks = (updates, options = {})=>taskCollection.batch((recorder)=>{
        updates.forEach(({ taskId, changes })=>recorder.update(taskId, changes));
    }, async ({ mutations })=>{
        const tasks = mutations.map((m)=>{
            var _m_changes;
            return {
                taskId: m.id,
                ...mapTaskChangesToPayload((_m_changes = m.changes) !== null && _m_changes !== void 0 ? _m_changes : {})
            };
        });
        await TaskAPI.bulkUpdateTask({
            tasks
        }, {
            isAllTask: options.isAllTask
        });
    });
/** Refetch a single task from the server and push it into the collection. */ export const syncTaskFromServer = async (projectId, taskId)=>{
    const task = await TaskAPI.getTask(projectId, taskId);
    taskCollection.push([
        task
    ]);
};
/** Refetch several tasks and push them in one batch. */ export const syncTasksFromServer = async (projectId, taskIds)=>{
    const tasks = await Promise.all(taskIds.map((taskId)=>TaskAPI.getTask(projectId, taskId)));
    taskCollection.push(tasks);
};
/**
 * Imperative persistence wrappers — call the endpoint, mirror the server
 * response into the collection, and RETURN the result. Use these where the
 * caller needs the updated `Task` synchronously (sagas that `yield`, React
 * Query mutationFns, components that read the response) and manages its own
 * optimistic state. Fire-and-forget optimistic mutations should use
 * `taskCollection.update/insert/delete` instead.
 */ export const createTaskRecord = async (...args)=>{
    const task = await TaskAPI.createTask(...args);
    taskCollection.push([
        task
    ]);
    return task;
};
export const updateTaskRecord = async (...args)=>{
    const task = await TaskAPI.updateTask(...args);
    taskCollection.push([
        task
    ]);
    return task;
};
export const updateTaskMiniRecord = async (...args)=>{
    const task = await TaskAPI.updateTaskMini(...args);
    taskCollection.push([
        task
    ]);
    return task;
};
/**
 * The project-plan swimlane renders a phase's cards from
 * `projectPhase.entityOrder` (a `Project`-level array of `"TASK_<id>"` /
 * `"SPRINT_<id>"` keys), NOT from the task list itself — see
 * `getUpdatedEntityOrder`/`useNativeDNDHandlers` in `swimlaneV2.tsx`. That
 * array only refreshes via a full project refetch, so a task moved to a new
 * phase is reactively correct in `taskCollection` (renders removed from the
 * old swimlane) but invisible in the new one until the network round-trip
 * lands. Patch both phases' `entityOrder` in `projectCollection` the same
 * instant, via `push` (a synced write, no API call — the server already
 * persists the real order under the hood via the phase-update endpoint; this
 * is purely a client-side visibility fix-up) so the swimlane renders it
 * immediately. The authoritative order still arrives when
 * `useUpdatePhaseTaskMutation`'s `fetchProjectDetail` resolves and replaces
 * this local patch wholesale.
 */ const reorderTaskEntityOptimistically = (projectId, taskId, fromPhaseId, toPhaseId)=>{
    const project = getProjectFromCollection(projectId);
    if (!(project === null || project === void 0 ? void 0 : project.projectPhases)) {
        return;
    }
    const entityId = converToEntityId(taskId, PhaseEntityType.Task);
    let changed = false;
    const nextPhases = project.projectPhases.map((phase)=>{
        var _phase_entityOrder, _phase_entityOrder1;
        if (phase.projectPhaseId === fromPhaseId && ((_phase_entityOrder = phase.entityOrder) === null || _phase_entityOrder === void 0 ? void 0 : _phase_entityOrder.includes(entityId))) {
            changed = true;
            return {
                ...phase,
                entityOrder: phase.entityOrder.filter((e)=>e !== entityId)
            };
        }
        if (phase.projectPhaseId === toPhaseId && !((_phase_entityOrder1 = phase.entityOrder) === null || _phase_entityOrder1 === void 0 ? void 0 : _phase_entityOrder1.includes(entityId))) {
            var _phase_entityOrder2;
            changed = true;
            return {
                ...phase,
                entityOrder: [
                    ...(_phase_entityOrder2 = phase.entityOrder) !== null && _phase_entityOrder2 !== void 0 ? _phase_entityOrder2 : [],
                    entityId
                ]
            };
        }
        return phase;
    });
    if (changed) {
        projectCollection.push([
            {
                ...project,
                projectPhases: nextPhases
            }
        ]);
    }
};
/**
 * Optimistic + returns the entity (pattern P6). `taskCollection.update`
 * routes a `projectPhase` patch to `TaskAPI.updatePhase` internally (see
 * `routeUpdate` in `task-collection.ts`), applying the overlay INSTANTLY —
 * any collection-driven view (e.g. the project-plan swimlanes) re-files the
 * task into its new phase immediately, instead of waiting on the PUT.
 */ export const updatePhaseRecord = async (taskId, payload)=>{
    var _payload_projectPhase, _existing_projectPhase, _existing_project;
    const existing = taskCollection.get(taskId);
    if (existing === undefined) {
        // Not in the collection — no overlay base; direct write + push.
        const task = await TaskAPI.updatePhase(taskId, payload);
        taskCollection.push([
            task
        ]);
        return task;
    }
    const toPhaseId = (_payload_projectPhase = payload.projectPhase) === null || _payload_projectPhase === void 0 ? void 0 : _payload_projectPhase.projectPhaseId;
    const fromPhaseId = (_existing_projectPhase = existing.projectPhase) === null || _existing_projectPhase === void 0 ? void 0 : _existing_projectPhase.projectPhaseId;
    const projectId = (_existing_project = existing.project) === null || _existing_project === void 0 ? void 0 : _existing_project.projectId;
    if (projectId != null && toPhaseId != null && fromPhaseId !== toPhaseId) {
        reorderTaskEntityOptimistically(projectId, taskId, fromPhaseId, toPhaseId);
    }
    const tx = taskCollection.update(taskId, payload);
    await tx.isPersisted;
    return taskCollection.get(taskId);
};
export const updatePrivacyRecord = async (...args)=>{
    const task = await TaskAPI.updatePrivacy(...args);
    taskCollection.push([
        task
    ]);
    return task;
};
export const updateSubTaskOrderRecord = (...args)=>TaskAPI.updateSubTaskOrder(...args);
/**
 * Imperative bulk update — forwards the full `BulkTaskPayload` (including
 * `operationName` / `bulkShiftDates`) to the endpoint. Use where the caller
 * manages its own optimistic state; for AgGrid-style optimistic bulk edits use
 * `bulkUpdateTasks` (which applies an optimistic overlay) instead.
 */ export const bulkUpdateTasksRecord = (...args)=>TaskAPI.bulkUpdateTask(...args);
/** Delete a task on the server and remove it from the collection. */ export const deleteTaskRecord = async (...args)=>{
    const [, taskId] = args;
    await TaskAPI.deleteTask(...args);
    taskCollection.externalDelete(taskId);
};
/**
 * Submit a CSAT rating. Not a task-field mutation (the endpoint returns a
 * boolean), so it runs outside the optimistic system; refetch reconciles the
 * task's `csatRatings`.
 */ export const submitTaskRating = async (taskId, data, projectId)=>{
    var _taskCollection_get_project, _taskCollection_get;
    await TaskAPI.addRating(taskId, data);
    const resolvedProjectId = projectId !== null && projectId !== void 0 ? projectId : (_taskCollection_get = taskCollection.get(taskId)) === null || _taskCollection_get === void 0 ? void 0 : (_taskCollection_get_project = _taskCollection_get.project) === null || _taskCollection_get_project === void 0 ? void 0 : _taskCollection_get_project.projectId;
    if (resolvedProjectId != null) {
        await syncTaskFromServer(resolvedProjectId, taskId);
    }
};
/**
 * Duplicate a task N times via the dedicated endpoint. Returns no bodies
 * (`Promise<void>`); the resulting tasks arrive via a WS refetch, so there is
 * nothing to push here.
 */ export const duplicateTask = (...args)=>TaskAPI.duplicateTask(...args);
/** Create a task from a form submission → push the returned task. */ export const createTaskWithForm = async (...args)=>{
    const task = await TaskAPI.createTaskWithForm(...args);
    taskCollection.push([
        task
    ]);
    return task;
};
/** Idempotent create (X-Request-Id) → push the returned task. */ export const createTaskWithRequestId = async (...args)=>{
    const task = await TaskAPI.createTaskWithRequestId(...args);
    taskCollection.push([
        task
    ]);
    return task;
};
/** Add tasks to a project from templates → push the returned tasks. */ export const addTaskTemplatesToProject = async (...args)=>{
    const result = await TaskAPI.addTaskTemplatesToProject(...args);
    taskCollection.push(result);
    return result;
};
/**
 * Bulk-create top-level tasks under a project. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task[]`.
 */ export const addMultipleTasks = async (...args)=>{
    const result = await TaskAPI.addMultipleTasks(...args);
    if (isTaskArray(result)) {
        taskCollection.push(result);
    }
    return result;
};
/**
 * Bulk-create subtasks under a parent task. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task[]`.
 */ export const addMultipleSubtasks = async (...args)=>{
    const [parentTaskId] = args;
    try {
        const result = await TaskAPI.addMultipleSubtasks(...args);
        if (isTaskArray(result)) {
            taskCollection.push(result);
        }
        if (typeof parentTaskId === 'number') {
            taskFunnel.subtaskAdd({
                parentTaskId,
                optimistic: false
            });
        }
        return result;
    } catch (error) {
        if (typeof parentTaskId === 'number') {
            taskFunnel.subtaskAddError({
                parentTaskId
            });
        }
        throw error;
    }
};
/**
 * Merge only the `dependentTasks` relation off a freshly-fetched task into
 * whatever's currently cached — never the whole snapshot. Both
 * `createDependentTask` and `updateDependentTasks` fetch the task server-side
 * (`getTaskDetails`) *before* their `@AfterReturning` AOP date-shift cascade
 * runs, so the response always carries pre-cascade dates. A full-task push
 * (`push()` always replaces — `isNewer` is unconditionally `true`) would
 * clobber whatever dates the client just computed optimistically for the
 * same task, which is exactly the "dates revert a moment after the
 * dependency is created" bug this avoids.
 */ const mergeDependentTasksIntoCollection = (fresh)=>{
    const current = taskCollection.get(fresh.taskId);
    if (!current) {
        return;
    }
    taskCollection.push([
        {
            ...current,
            dependentTasks: fresh.dependentTasks
        }
    ]);
};
/**
 * Create a dependency between tasks. The `add-dependency` endpoint actually
 * returns an echo of the request body (`TaskToDependentTaskModel`), not a
 * `Task` — `TaskAPI.createDependentTask`'s declared return type is a lie.
 * Refetch the real task to reconcile `dependentTasks` (see
 * `mergeDependentTasksIntoCollection` for why only that field is merged).
 *
 * The refetch is best-effort: the dependency was already created server-side
 * by the call above, and callers (the gantt drag saga has no try/catch; the
 * task-pane caller has no `.catch`) would otherwise have an uncaught
 * rejection abort their whole follow-up flow — or, worse, crash the saga
 * middleware — over a reconciliation fetch that isn't on the critical path.
 */ export const createDependentTask = async (...args)=>{
    const [projectId, taskId] = args;
    const result = await TaskAPI.createDependentTask(...args);
    try {
        const fresh = await TaskAPI.getTask(projectId, taskId);
        mergeDependentTasksIntoCollection(fresh);
    } catch (error) {
        logger.error('createDependentTask: failed to refetch task', {
            error,
            projectId,
            taskId
        });
    }
    return result;
};
/**
 * Remove a dependency between tasks. Returns a boolean; callers should refetch
 * the affected task(s) via `syncTasksFromServer` to reconcile state.
 */ export const removeDependentTask = (...args)=>TaskAPI.removeDependentTask(...args);
/**
 * Update a task's dependent tasks. Returns the updated `Task`, which — per
 * `mergeDependentTasksIntoCollection` — only has its `dependentTasks` merged
 * into the collection; the returned dates are pre-cascade and must not
 * overwrite the client-computed optimistic dates. Callers should still
 * refetch the other affected task(s) via `syncTasksFromServer` since the
 * response only carries the directly-edited task.
 */ export const updateDependentTasks = async (...args)=>{
    const result = await TaskAPI.updateDependentTasks(...args);
    mergeDependentTasksIntoCollection(result);
    return result;
};
/**
 * Promote a subtask back to a top-level task. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task`.
 */ export const removeAsSubtask = async (...args)=>{
    const result = await TaskAPI.removeAsSubtask(...args);
    if (isTask(result)) {
        taskCollection.push([
            result
        ]);
    }
    return result;
};
/** Delete a task attachment. No `Task` entity returned — just forward. */ export const deleteTaskAttachment = (...args)=>TaskAPI.deleteTaskAttachment(...args);
/**
 * Bulk-update tasks for a select-all operation. The endpoint returns no per-id
 * bodies, so there is nothing to push; visible state stays optimistic until a
 * WS refetch reconciles it. Just forward.
 */ export const bulkUpdateAllTasks = (...args)=>TaskAPI.bulkUpdateAllTasks(...args);
